/**
 * GolfTours — home section that lists configured pro golf tours.
 * Each card shows the tour logo/name and a clear CTA that opens the tour's
 * official site in a new tab. Live leaderboard agent is parked for the future
 * (a paid API like SlashGolf would be the right path — see PRD).
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const COPY = {
  ca: { title: 'Golf Tours', subtitle: 'Visita els circuits professionals', cta: 'Veure al web oficial' },
  es: { title: 'Golf Tours', subtitle: 'Visita los circuitos profesionales', cta: 'Ver en la web oficial' },
  en: { title: 'Golf Tours', subtitle: 'Visit the pro circuits',             cta: 'Visit official site' },
  fr: { title: 'Golf Tours', subtitle: 'Visite les circuits pros',           cta: 'Voir sur le site officiel' },
};
const pickCopy = (lang) => COPY[(lang || 'ca').split('-')[0]] || COPY.ca;

export const GolfTours = () => {
  const { i18n } = useTranslation();
  const copy = pickCopy(i18n.language);
  const [tours, setTours] = useState([]);

  useEffect(() => {
    axios.get(`${API_URL}/api/tours`).then((r) => setTours(r.data || [])).catch(() => {});
  }, []);

  if (!tours.length) return null;

  return (
    <section className="relative z-10 mt-20 mb-12 px-5 md:px-10 w-full" data-testid="golf-tours-section">
      <div className="max-w-5xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="text-center"
        >
          <div className="font-sans text-[10px] tracking-[0.32em] uppercase text-[#D4AF37]">🏌️ Circuits</div>
          <h2 className="mt-2 font-serif text-3xl md:text-4xl tracking-tight text-[#F2E8D5]">{copy.title}</h2>
          <p className="mt-2 text-[#A8B4A6] italic">{copy.subtitle}</p>
        </motion.div>

        <div className="mt-10 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
          {tours.map((t) => (
            <motion.a
              key={t.slug}
              href={t.official_url || t.leaderboard_url}
              target="_blank"
              rel="noopener noreferrer"
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              whileHover={{ y: -4, scale: 1.02 }}
              className="bg-[#083B1F]/60 border border-[#D4AF37]/15 hover:border-[#D4AF37]/50 rounded-xl p-5 backdrop-blur-sm flex flex-col items-center text-center transition-colors group"
              data-testid={`tour-card-${t.slug}`}
            >
              <div className="h-14 flex items-center justify-center">
                {t.logo_url ? (
                  <img src={t.logo_url} alt={t.name} className="max-h-14 max-w-full object-contain" />
                ) : (
                  <span className="font-serif text-lg text-[#F2E8D5] tracking-tight">{t.name}</span>
                )}
              </div>
              <span className="mt-4 inline-flex items-center gap-1.5 text-[#D4AF37] group-hover:text-[#E5C158] font-sans tracking-[0.18em] uppercase text-[10px] transition-colors">
                <span>↗</span> {copy.cta}
              </span>
            </motion.a>
          ))}
        </div>
      </div>
    </section>
  );
};

export default GolfTours;
